import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Button, type ButtonProps } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2 } from "lucide-react";

const registerSchema = z.object({
  full_name: z.string().min(1, "Full name is required"),
  email: z.string().email("Please enter a valid email address"),
  phone_number: z.string().min(1, "Phone number is required"),
  city: z.string().min(1, "City is required"),
  work_entity: z.string().min(1, "Work entity / company is required"),
});

type RegisterValues = z.infer<typeof registerSchema>;

interface RegisterDialogProps {
  /** Styling / content for the trigger button. */
  triggerClassName?: string;
  triggerSize?: ButtonProps["size"];
  triggerVariant?: ButtonProps["variant"];
  triggerLabel?: string;
  /** Optional event context tied to this registration. */
  eventId?: number;
  eventTitle?: string;
}

export function RegisterDialog({
  triggerClassName,
  triggerSize = "sm",
  triggerVariant = "default",
  triggerLabel = "Register",
  eventId,
  eventTitle,
}: RegisterDialogProps) {
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm<RegisterValues>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterValues) => {
    try {
      const response = await api.post("/api/registrations", {
        ...data,
        event_id: eventId ?? null,
        event_title: eventTitle ?? null,
      });
      if (response.success) {
        toast.success("Thank you for registering! We'll be in touch soon.");
        setSubmitted(true);
        reset();
      } else {
        toast.error(response.error || response.message || "Something went wrong.");
      }
    } catch (error) {
      console.error("Registration error:", error);
      toast.error("Network error — please try again.");
    }
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    // Reset the thank-you screen when the dialog closes so a reopen starts fresh.
    if (!next) {
      setTimeout(() => setSubmitted(false), 200);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Button
        type="button"
        size={triggerSize}
        variant={triggerVariant}
        className={triggerClassName}
        onClick={() => setOpen(true)}
      >
        {triggerLabel}
      </Button>

      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        {submitted ? (
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent/15">
              <CheckCircle2 className="h-9 w-9 text-accent" />
            </div>
            <DialogHeader className="items-center">
              <DialogTitle className="text-2xl text-primary">Thank You for Registering!</DialogTitle>
              <DialogDescription className="text-base">
                Your registration enquiry has been received. Our team will get back to you soon.
              </DialogDescription>
            </DialogHeader>
            <Button
              className="mt-2 bg-primary hover:bg-primary/90"
              onClick={() => setOpen(false)}
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl text-primary">Register for the Event</DialogTitle>
              <DialogDescription>
                Fill in your details below and we'll get back to you to confirm your registration.
                {eventTitle ? (
                  <span className="block mt-1 font-medium text-foreground">{eventTitle}</span>
                ) : null}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reg-full-name">Full Name *</Label>
                <Input id="reg-full-name" {...register("full_name")} disabled={isSubmitting} />
                {errors.full_name && (
                  <p className="text-xs text-destructive">{errors.full_name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="reg-email">Email *</Label>
                <Input
                  id="reg-email"
                  type="email"
                  {...register("email")}
                  disabled={isSubmitting}
                />
                {errors.email && (
                  <p className="text-xs text-destructive">{errors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="reg-phone">Phone Number *</Label>
                <Input id="reg-phone" type="tel" {...register("phone_number")} disabled={isSubmitting} />
                {errors.phone_number && (
                  <p className="text-xs text-destructive">{errors.phone_number.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="reg-city">City *</Label>
                <Input id="reg-city" {...register("city")} disabled={isSubmitting} />
                {errors.city && <p className="text-xs text-destructive">{errors.city.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="reg-entity">Work Entity / Company *</Label>
                <Input id="reg-entity" {...register("work_entity")} disabled={isSubmitting} />
                {errors.work_entity && (
                  <p className="text-xs text-destructive">{errors.work_entity.message}</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Registration"
                )}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
